import { useEffect, useRef, useState } from "react";
import { add3DBuildingsLayer, clearLayers } from "../../../utils/layerUtils";
import mapboxGl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { ACCESS_TOKEN } from "../../../constants/accessTokens";
import { drawStyles } from "../../../constants/drawStyles";
import { clearBufferLayers } from "../../../utils/bufferUtils";
import { MapContext } from "../../../hooks/useMap";
import { DrawContext } from "../../../hooks/useDraw";
import styled, { keyframes } from "styled-components";
import Overlay from "./Overlay";

export default function Map({ isFullScreen, handleFullScreen }) {
  const [mapContext, setMapContext] = useState(null);
  const [drawContext, setDrawContext] = useState(null);
  const [drawMode, setDrawMode] = useState({
    area: "simple_select",
    home: "simple_select",
    point: "simple_select",
  });
  const [isCalculated, setIsCalculated] = useState(false);
  const [result, setResult] = useState(null);
  const [calculatedResult, setCalculatedResult] = useState(0);
  const clearRef = useRef(null);
  const [bearing, setBearing] = useState(0);
  const toggleDrawModeRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    mapboxGl.accessToken = ACCESS_TOKEN;

    const map = new mapboxGl.Map({
      container: mapRef.current || "",
      style: `mapbox://styles/mapbox/light-v10`,
      center: [11.632701, 48.142146],
      zoom: 4,
      interactive: true,
      maxTileCacheSize: 100000,
      refreshExpiredTiles: true,
      preserveDrawingBuffer: true,
    });
    setMapContext(map);

    const draw = new MapboxDraw({
      displayControlsDefault: false,
      styles: drawStyles,
    });

    map.addControl(draw);
    setDrawContext(draw);

    const scale = new mapboxGl.ScaleControl();
    map.addControl(scale);

    const scaleContainer = scale._container;

    scaleContainer.parentNode.removeChild(scaleContainer);
    map.getContainer().appendChild(scaleContainer);
    scaleContainer.style.position = "absolute";
    scaleContainer.style.bottom = "15px";
    scaleContainer.style.right = "15px";

    map.on("rotate", () => {
      setBearing(map.getBearing());
    });

    map.scrollZoom.disable();
    map.touchZoomRotate.disable();

    map.on("wheel", (event) => {
      if (event.originalEvent.ctrlKey) {
        map.scrollZoom.enable();
      } else {
        map.scrollZoom.disable();
      }
    });

    map.on("style.load", async () => {
      map.addLayer({
        id: "landcover-2",
        source: "composite",
        "source-layer": "landcover",
        type: "fill",
        paint: {
          "fill-color": [
            "match",
            ["get", "class"],
            "wood",
            "rgba(30, 109, 3, 0.8)",
            "scrub",
            "rgba(163, 212, 135, 0.6)",
            "crop",
            "rgba(198, 253, 18, 0.6)",
            "grass",
            "rgba(178, 227, 150, 0.6)",
            "rgba(0, 0, 0, 0)",
          ],
          "fill-opacity": 0.5,
          "fill-outline-color": "rgba(0,0,0,0)",
        },
      });
      map.addSource("satellite-source", {
        type: "raster",
        url: "mapbox://mapbox.satellite",
        tileSize: 256,
      });

      map.addLayer({
        id: "satellite-layer",
        type: "raster",
        source: "satellite-source",
        paint: {
          "raster-opacity": 1,
        },
      });

      add3DBuildingsLayer(map);
    });

    toggleDrawModeRef.current = (drawMode) => {
      draw.changeMode(draw.getMode() === "simple_select" ? drawMode : "simple_select");
    };

    clearRef.current = () => {
      clearLayers(map);
      clearBufferLayers(map);
      setDrawMode((prev) => ({
        ...prev,
        area: "simple_select",
      }));
      setResult(null);
      setCalculatedResult(0);
      setIsCalculated(false);
      draw.changeMode("simple_select");
      draw.deleteAll();
    };
  }, [clearRef, setBearing, setCalculatedResult, setDrawMode, setIsCalculated, setResult, toggleDrawModeRef, setMapContext, setDrawContext]);

  return (
    <MapContext.Provider value={mapContext}>
      <DrawContext.Provider value={drawContext}>
        <StyledMap $isFullScreen={isFullScreen}>
          <div id="map" ref={mapRef}></div>
          {mapContext && drawContext && (
            <Overlay
              result={result}
              setResult={setResult}
              setDrawMode={setDrawMode}
              drawMode={drawMode}
              toggleDrawModeRef={toggleDrawModeRef}
              clearRef={clearRef}
              isCalculated={isCalculated}
              setIsCalculated={setIsCalculated}
              setCalculatedResult={setCalculatedResult}
              bearing={bearing}
              calculatedResult={calculatedResult}
              isFullScreen={isFullScreen}
              handleFullScreen={handleFullScreen}
            />
          )}
        </StyledMap>
      </DrawContext.Provider>
    </MapContext.Provider>
  );
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const StyledMap = styled.div`
  ${(props) =>
    props.$isFullScreen
      ? `
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100%;
    z-index: 999;
    overflow-y: hidden;
  `
      : `
    position: relative;
    height: 600px;
    width: 100%;
    border-radius: 20px;
  `}

  overflow: hidden;
  display: flex;
  font-family: "Open Sans", sans-serif;
  font-style: normal;

  #map {
    width: 100%;
    height: 100%;
    flex-grow: 1;
  }

  .mapboxgl-ctrl-bottom-right,
  .mapboxgl-ctrl-bottom-left {
    display: none;
  }

  .contact-modal {
    width: 263px;
    position: absolute;
    right: 15px;
    top: 15px;
    z-index: 2;
    animation: ${fadeIn} 0.3s forwards;
  }
`;
